import React, { LegacyRef, useMemo } from 'react';
import { Button, Combobox, Flex } from '@highereducation/hen-ui';
import { QuestionProps } from '@de/components/Step/Step';
import { Option } from '@de/types/types';
import { useDynamicExperienceContext } from '@de/providers/DynamicExperienceProvider';
import useQuestionEvents from '@de/hooks/useQuestionEvents';
import { OptionsLoadingPlaceholder } from '@de/components/Step/OptionsLoadingPlaceholder';
import { ErrorMessage } from '@de/components/ErrorMessage';
import {
  optionToComboboxOption,
  partitionSearchBarOptions,
} from '@de/utils/searchBarOptions';

const SingleChoicePillsQuestion: React.FC<QuestionProps> = ({
  options = [],
  id = '',
  leadFieldAssociated,
  automaticNext = false,
  isMultiQuestionChild = false,
  title,
  stepName,
  variant,
  isOptionsLoading = false,
  searchBarAllOptions,
  dynamicOptionsError,
}) => {
  const { updateFlowState, getStepMetadataById, handleStepForward } =
    useDynamicExperienceContext();
  const stepSelectedData = getStepMetadataById(id || '');
  const selectedOption = stepSelectedData?.option as Option;

  const { ref: visibilityRef, triggerFieldInputted } = useQuestionEvents({
    id,
    fieldType: 'single_choice',
    options,
    title,
    stepName,
    isMultiQuestionChild,
    variant: variant || 'single_choice_pills',
    leadFieldAssociated,
  });

  const hasSearchBar = Boolean(searchBarAllOptions?.length);

  const isSingleSelected =
    selectedOption && !Array.isArray(selectedOption)
      ? selectedOption
      : undefined;

  const { displayedOptions, searchableOptions } = useMemo(() => {
    if (!hasSearchBar) {
      return { displayedOptions: options, searchableOptions: [] as Option[] };
    }
    return partitionSearchBarOptions(
      options,
      searchBarAllOptions as Option[],
      isSingleSelected ? [isSingleSelected] : [],
    );
  }, [hasSearchBar, options, searchBarAllOptions, isSingleSelected]);

  const selectOption = (option: Option) => {
    updateFlowState(id, { option, leadFieldAssociated });
    if (automaticNext && !isMultiQuestionChild) {
      handleStepForward();
    }
    triggerFieldInputted(option.text as string);
  };

  const handleSearchSelect = (value: string) => {
    const option = (searchBarAllOptions ?? []).find(
      (opt) => opt.optionId === value,
    );
    if (option) {
      selectOption(option);
    }
  };

  const showLoading = isOptionsLoading && !dynamicOptionsError;

  return (
    <>
      {dynamicOptionsError ? (
        <ErrorMessage
          message={dynamicOptionsError}
          className='w-full justify-center py-2 px-2'
        />
      ) : null}
      <Flex
        gap='sm'
        wrap='wrap'
        align='center'
        justify='start'
        direction='row'
        className='w-full'
        ref={visibilityRef as LegacyRef<HTMLDivElement>}
      >
        {showLoading ? (
          <OptionsLoadingPlaceholder />
        ) : (
          displayedOptions.map((option, index) => {
            const isSelected = isSingleSelected?.optionId === option.optionId;
            return (
              <Button
                key={index}
                size='sm'
                iconBefore={isSelected ? 'check' : undefined}
                variant='grayOutline'
                aria-pressed={isSelected}
                className='w-fit'
                onClick={() => {
                  if (isSelected) {
                    updateFlowState(id, null);
                  } else {
                    updateFlowState(id, { option, leadFieldAssociated });
                    if (automaticNext && !isMultiQuestionChild) {
                      handleStepForward();
                    }
                  }

                  triggerFieldInputted(option.text as string);
                }}
              >
                {option.text}
              </Button>
            );
          })
        )}
      </Flex>
      {!showLoading && hasSearchBar && searchableOptions.length > 0 && (
        <Combobox
          className='mt-4'
          aria-label={title || 'Search options'}
          options={searchableOptions.map(optionToComboboxOption)}
          selectedValues={isSingleSelected ? [isSingleSelected.optionId] : []}
          onSelect={handleSearchSelect}
        />
      )}
    </>
  );
};

export default SingleChoicePillsQuestion;
